import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, CalendarPlus, Trash2, Lightbulb, Star } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function StudioOpsPanel() {
  const [events, setEvents] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', description: '', audience: 'tous', capacity: '' });
  const [opening, setOpening] = useState(null);
  const [regs, setRegs] = useState({});

  const load = useCallback(async () => {
    try {
      const [e, s, f, o] = await Promise.all([
        axios.get(`${API}/admin/events`, { withCredentials: true }),
        axios.get(`${API}/admin/suggestions`, { withCredentials: true }),
        axios.get(`${API}/admin/feedback`, { withCredentials: true }),
        axios.get(`${API}/admin/opening-date`, { withCredentials: true }),
      ]);
      setEvents(e.data.events); setSuggestions(s.data.suggestions); setFeedback(f.data); setOpening(o.data);
    } catch (e) { toast.error('Chargement impossible.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveOpening = async (date) => {
    try {
      await axios.put(`${API}/admin/opening-date`, { opening_date: date || null }, { withCredentials: true });
      toast.success(date ? `Date d'ouverture officielle enregistrée : ${date}. Les vraies dates de séances s'activeront à partir de ce jour.` : 'Mode pré-ouverture réactivé.');
      load();
    } catch (e) { toast.error('Erreur.'); }
  };

  const showRegs = async (eventId) => {
    if (regs[eventId]) { setRegs({ ...regs, [eventId]: null }); return; }
    const r = await axios.get(`${API}/admin/events/${eventId}/registrations`, { withCredentials: true });
    setRegs({ ...regs, [eventId]: r.data.registrations });
  };

  const cancelReg = async (eventId, regId) => {
    if (!window.confirm('Annuler cette inscription ? La place sera libérée (et attribuée à la liste d\'attente le cas échéant).')) return;
    const r = await axios.delete(`${API}/admin/events/${eventId}/registrations/${regId}`, { withCredentials: true });
    toast.success(r.data.promoted ? `Inscription annulée — ${r.data.promoted} récupère la place.` : 'Inscription annulée, place libérée.');
    setRegs({ ...regs, [eventId]: null });
    load();
  };

  const setEventDate = async (eventId) => {
    const d = window.prompt('Date officielle (AAAA-MM-JJ) — laissez vide pour annuler :');
    if (!d) return;
    await axios.put(`${API}/admin/events/${eventId}`, { date: d }, { withCredentials: true });
    toast.success('Date enregistrée. Vous pourrez maintenant communiquer aux inscrites.');
    load();
  };

  const createEvent = async () => {
    if (!form.title) return toast.error('Titre requis.');
    try {
      await axios.post(`${API}/admin/events`, { ...form, date: form.date || null, capacity: form.capacity ? parseInt(form.capacity, 10) : null }, { withCredentials: true });
      toast.success('Événement créé et visible dans les espaces concernés.');
      setForm({ title: '', date: '', description: '', audience: 'tous', capacity: '' });
      load();
    } catch (e) { toast.error('Erreur.'); }
  };

  const delEvent = async (id) => {
    await axios.delete(`${API}/admin/events/${id}`, { withCredentials: true });
    load();
  };

  const setStatus = async (id, status) => {
    await axios.put(`${API}/admin/suggestions/${id}`, { status }, { withCredentials: true });
    load();
  };

  if (!events) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></div>;

  const RESP = { participe: 'Participe', ne_participe_pas: 'Ne participe pas', a_confirmer: 'À confirmer' };

  return (
    <div className="space-y-5" data-testid="studio-ops-panel">
      {/* Date d'ouverture officielle */}
      {opening && (
        <div className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid="opening-date-block">
          <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Date d'ouverture officielle du studio</p>
          {opening.pre_opening ? (
            <p className="text-xs text-[#9A744C] mb-2">⏳ Mode pré-ouverture actif — les créneaux clientes sont des <strong>pré-réservations</strong> : aucune vraie date de séance n'est générée, aucun rappel de séance n'est envoyé.</p>
          ) : (
            <p className="text-xs text-[#5C7057] mb-2">✓ Studio ouvert depuis le {opening.opening_date}.</p>
          )}
          <div className="flex items-center gap-2">
            <Input type="date" defaultValue={opening.opening_date || ''} id="opening-date-input" className="rounded-xl border-[#E9DDCC] w-44" data-testid="opening-date-input" />
            <Button onClick={() => saveOpening(document.getElementById('opening-date-input').value)} className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-full h-9 text-xs" data-testid="opening-date-save">Enregistrer</Button>
            {opening.opening_date && <Button variant="outline" onClick={() => saveOpening(null)} className="rounded-full h-9 text-xs border-[#E9DDCC]">Repasser en pré-ouverture</Button>}
          </div>
        </div>
      )}

      {/* Événements */}
      <div className="bg-white border border-[#E9DDCC] rounded-2xl p-4 space-y-3" data-testid="admin-events-block">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold flex items-center gap-1"><CalendarPlus className="w-3 h-3" /> Événements PURO</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input placeholder="Titre de l'événement" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="event-title" />
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="event-date" />
          <Input type="number" placeholder="Capacité (facultatif)" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="event-capacity" />
        </div>
        <p className="text-[10px] text-[#AE9885]">Date facultative — sans date, l'événement s'affiche « Date à venir » et aucun rappel n'est envoyé.</p>
        <Textarea placeholder="Description (facultatif)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border-[#E9DDCC] min-h-[56px]" data-testid="event-desc" />
        <div className="flex items-center gap-2">
          {[['tous', 'Clientes + coachs'], ['clientes', 'Clientes'], ['coachs', 'Coachs']].map(([v, l]) => (
            <button key={v} onClick={() => setForm({ ...form, audience: v })}
              className={`text-xs px-3 py-1.5 rounded-full border ${form.audience === v ? 'bg-[#C99A55] text-white border-transparent' : 'border-[#E9DDCC] text-[#7A6455]'}`}>{l}</button>
          ))}
          <Button onClick={createEvent} className="ml-auto bg-[#9A744C] hover:bg-[#493329] text-white rounded-full h-9 text-xs" data-testid="event-create">Créer l'événement</Button>
        </div>
        {events.map((e) => (
          <div key={e.id} className="border-t border-[#EFE4D0] py-2" data-testid={`admin-event-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm text-[#493329]">{e.title} · {e.date ? new Date(e.date + 'T00:00').toLocaleDateString('fr-FR') : <span className="text-[#9A744C]">Date à venir</span>} <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5EFE5] text-[#7A6455]">{e.audience}</span>
                  {e.capacity != null && (
                    <span className="ml-1.5 text-[10px] px-2 py-0.5 rounded-full bg-[#F3EADA] text-[#9A744C]" data-testid={`admin-event-count-${e.id}`}>
                      {e.registered_count}/{e.capacity} inscrites · {e.remaining} restante(s){e.waitlist_count > 0 ? ` · ${e.waitlist_count} en attente` : ''}
                    </span>
                  )}
                </p>
                {e.responses.length > 0 && (
                  <p className="text-[11px] text-[#7A6455] mt-0.5">Coachs : {e.responses.map((r) => `${r.coach_name} — ${RESP[r.response]}`).join(' · ')}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {e.capacity != null && (
                  <>
                    <button onClick={() => showRegs(e.id)} className="text-xs text-[#9A744C] underline" data-testid={`event-regs-${e.id}`}>Participantes</button>
                    <a href={`${API}/admin/events/${e.id}/registrations/export`} className="text-xs text-[#7A6455] underline" data-testid={`event-export-${e.id}`}>Export CSV</a>
                    {!e.date && <button onClick={() => setEventDate(e.id)} className="text-xs text-[#5C7057] underline" data-testid={`event-setdate-${e.id}`}>Définir la date</button>}
                  </>
                )}
                <button onClick={() => delEvent(e.id)} className="text-[#C76542] p-1" data-testid={`event-del-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {regs[e.id] && (
              <div className="mt-2 bg-[#F5EFE5] rounded-xl p-3 space-y-1" data-testid={`event-reglist-${e.id}`}>
                {regs[e.id].length === 0 && <p className="text-xs text-[#AE9885]">Aucune inscription.</p>}
                {regs[e.id].map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-xs">
                    <span className="text-[#493329]">{r.first_name} {r.last_name} · {r.email}{r.phone ? ` · ${r.phone}` : ''} · {new Date(r.created_at).toLocaleDateString('fr-FR')}
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full ${r.status === 'inscrite' ? 'bg-[#EDF2EA] text-[#5C7057]' : 'bg-[#FBF3DC] text-[#9A744C]'}`}>{r.status}</span>
                    </span>
                    <button onClick={() => cancelReg(e.id, r.id)} className="text-[#C76542] underline" data-testid={`reg-cancel-${r.id}`}>Annuler</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Idées clientes */}
      <div className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid="admin-suggestions-block">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Vos idées pour PURO ({suggestions.length})</p>
        {suggestions.length === 0 && <p className="text-xs text-[#AE9885]">Aucune suggestion pour le moment.</p>}
        {suggestions.map((s) => (
          <div key={s.id} className="border-t border-[#EFE4D0] py-2 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm text-[#493329]">{s.text}</p>
              <p className="text-[11px] text-[#AE9885]">{s.name} · {new Date(s.created_at).toLocaleDateString('fr-FR')}</p>
            </div>
            <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)} className="text-xs border border-[#E9DDCC] rounded-lg px-2 py-1 bg-white" data-testid={`suggestion-status-${s.id}`}>
              <option value="nouvelle">Nouvelle</option><option value="lue">Lue</option><option value="retenue">Retenue</option>
            </select>
          </div>
        ))}
      </div>

      {/* Satisfaction */}
      {feedback && (
        <div className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid="admin-feedback-block">
          <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2 flex items-center gap-1"><Star className="w-3 h-3" /> Satisfaction post-séance ({feedback.count} réponse(s))</p>
          <div className="grid grid-cols-3 gap-3 mb-2">
            {[['Séance', feedback.avg_seance], ['Coach', feedback.avg_coach], ['Studio', feedback.avg_studio]].map(([l, v]) => (
              <div key={l} className="bg-[#F5EFE5] rounded-xl p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#7A6455]">{l}</p>
                <p className="font-heading text-xl text-[#493329]">{v || '—'}/5</p>
              </div>
            ))}
          </div>
          {feedback.feedback.slice(0, 10).map((f) => (
            <div key={f.id} className="border-t border-[#EFE4D0] py-1.5 text-xs text-[#7A6455]">
              {f.name} · {f.rating_seance}/{f.rating_coach}/{f.rating_studio}{f.comment ? ` — « ${f.comment} »` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
